<template>
  <div>
    <l-map :zoom="zoom" :center="center" class="custom-map">
      <l-tile-layer :url="tileUrl" :attribution="attribution"></l-tile-layer>

      <!-- Marker for the bus -->
      <l-marker
        v-if="buses[busId]"
        :lat-lng="[buses[busId].coordinate.lat, buses[busId].coordinate.long]"
        :icon="customIcon"
        :z-index-offset="1000"
      >
        <l-popup>Xe</l-popup>
      </l-marker>

      <!-- Polylines for each route -->
      <l-polyline :lat-lngs="routes" :weight="4">
        <l-popup>Lộ trình</l-popup>
      </l-polyline>

      <!-- Marker for the school -->
      <l-marker v-if="school && school.length" :lat-lng="[school[0], school[1]]" :icon="schoolIcon">
        <l-popup>Nhà trường</l-popup>
      </l-marker>

      <!-- Markers for the stops -->
      <l-marker
        v-for="(stop, index) in stops"
        :key="index"
        :lat-lng="stop.coordinate"
        :icon="createLocationIcon(index)"
        :z-index-offset="(stops.length - index) * 10"
      >
        <l-popup>{{ stop.address }}</l-popup>
      </l-marker>
    </l-map>
  </div>
</template>

<script>
import { Bus } from '../../plugins/api'
import { LMap, LTileLayer, LMarker, LPopup, LPolyline } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { icon } from 'leaflet'
import { database } from '../../../firebase'
import { ref, onValue } from 'firebase/database'
import customMarkerImage from '@/assets/icons/bus-side.svg'
import grayBusMarker from '@/assets/icons/busGray.svg'
import schoolMarker from '@/assets/icons/school.svg'
import { get } from 'lodash'

export default {
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LPolyline
  },
  props: {
    query: {
      type: Object
    }
  },
  data() {
    return {
      zoom: 16,
      center: [21.018805085346695, 105.8346690057317],
      routes: [],
      stops: [],
      school: [],
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      buses: {},
      customIcon: null,
      schoolIcon: null,
      locationIcon: null,
      busId: null,
      busRef: null,
      animationId: null,
      unsubscribeBus: null,
      busMarker: customMarkerImage,
      timestamp: Date.now(),
      iconConfig: {
        iconSize: [38, 38],
        iconAnchor: [19, 38],
        popupAnchor: [0, -38]
      },
      monitorInterval: null
    }
  },
  created() {
    this.createCustomIcon()
    this.createSchoolIcon()
    this.createLocationIcon()
  },
  beforeDestroy() {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval)
    }
  },

  watch: {
    query: {
      handler() {
        if (this.query && this.query.bus) {
          this.busId = this.query.bus
          this.buses = {}
          this.loadBus()
          this.fetchBusData()
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    moveToNewPosition(currentPosition, newPosition) {
      const duration = 10000 // Duration of the animation in milliseconds
      const fps = 120 // fps
      const totalFrames = (duration / 1000) * fps // Total number of frames
      const deltaLat = (newPosition.lat - currentPosition.lat) / totalFrames
      const deltaLng = (newPosition.lng - currentPosition.lng) / totalFrames

      // Generate a unique ID for this animation
      const currentAnimationId = Date.now()
      this.animationId = currentAnimationId

      let frame = 0
      const moveStep = () => {
        // Check and see if the animation is current one.
        if (this.animationId !== currentAnimationId) {
          return
        }
        // Animate the bus movement based on change of long/lat based on time/frame
        if (frame < totalFrames) {
          currentPosition.lat += deltaLat
          currentPosition.lng += deltaLng
          this.$set(this.buses[this.busId].coordinate, 'lat', currentPosition.lat)
          this.$set(this.buses[this.busId].coordinate, 'long', currentPosition.lng)
          frame++
          setTimeout(moveStep, 1000 / fps)
        }
      }
      moveStep()
    },

    loadBus() {
      if (this.unsubscribeBus) {
        this.unsubscribeBus()
      }
      // Get bus data from firebase + watcher.
      const dbRef = ref(database, `buses/${this.busId}`)
      this.unsubscribeBus = onValue(dbRef, snapshot => {
        if (snapshot.exists()) {
          if (this.busId === snapshot.key) {
            const busData = snapshot.val()
            const lat = get(busData, 'coordinate.lat', 21.018805085346695)
            const long = get(busData, 'coordinate.long', 105.8346690057317)
            const timestamp = get(busData, 'timestamp', Date.now())

            if (this.buses[this.busId]) {
              const currentPosition = {
                lat: this.buses[this.busId].coordinate.lat,
                lng: this.buses[this.busId].coordinate.long
              }
              const newPosition = { lat, lng: long }
              this.moveToNewPosition(currentPosition, newPosition)
            } else {
              this.$set(this.buses, this.busId, busData)
              this.center = [lat, long]
            }

            this.timestamp = timestamp
            this.checkTimestampAndUpdateIcon()
            this.startMonitoringTimestamp()
          }
        }
      })
    },

    createCustomIcon() {
      this.customIcon = icon({
        iconUrl: this.busMarker,
        ...this.iconConfig
      })
    },
    createSchoolIcon() {
      this.schoolIcon = icon({
        iconUrl: schoolMarker,
        ...this.iconConfig
      })
    },
    createLocationIcon(index) {
      const svgString = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4CAF50" width="38px" height="38px">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="#ffffff" stroke-width="1"/>
    <text x="12" y="10" text-anchor="middle" fill="#ffffff" font-size="9" font-weight="bold" font-family="Arial" dy=".3em">${index +
      1}</text>
  </svg>`
      const svgUrl = `data:image/svg+xml;base64,${btoa(svgString)}`
      return icon({
        iconUrl: svgUrl,
        ...this.iconConfig
      })
    },
    startMonitoringTimestamp() {
      this.monitorInterval = setInterval(() => {
        this.checkTimestampAndUpdateIcon()
      }, 60000)
    },

    async fetchBusData() {
      this.$loading.active = true
      const busData = await Bus.getBusStopMapping(this.busId, {
        direction: this.query.direction,
        periodTime: this.query.periodTime
      })
      if (busData) {
        this.routes = busData.routes
        this.stops = busData.stops
        this.school = busData.school
      } else {
        this.$alert.error('Không tìm được thông tin xe')
      }
      this.$loading.active = false
    },
    checkTimestampAndUpdateIcon() {
      const currentTime = Date.now()
      const timeDifference = (currentTime - this.timestamp) / 1000 / 60
      if (timeDifference > 5) {
        this.busMarker = grayBusMarker
      } else {
        this.busMarker = customMarkerImage
      }
      this.createCustomIcon()
    }
  }
}
</script>

<style scoped>
.custom-map {
  z-index: 0 !important;
  height: 500px;
}
.grayed-out {
  filter: grayscale(100%);
}
</style>
